/**
 * FbDropdown — Figma-spec dropdown built on React Aria Components.
 *
 * Trigger: white pill button with border, dark text, caret icon.
 * Menu: white card with rounded-xl, soft shadow, hover-gray items.
 *
 * Figma reference: node 7216-88715 (First Bite Design System)
 *
 * Usage:
 *   <FbDropdown
 *     label="Last 6 Months"
 *     items={[
 *       { key: '3m', label: 'Last 3 Months' },
 *       { key: '6m', label: 'Last 6 Months' },
 *     ]}
 *     onAction={(key) => setRange(key as PerformanceRange)}
 *   />
 */
import React from 'react'
import {
  Button,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  Section,
  Separator,
} from 'react-aria-components'
import { PiCaretDownBold } from 'react-icons/pi'
import { cn } from '../UI/cn'

export interface FbDropdownItem {
  key: string
  label: string
  /** Optional icon rendered before the label. */
  icon?: React.ReactNode
}

export interface FbDropdownSection {
  key: string
  /** Accessible label for the section (rendered as `aria-label` on the
   *  grouping element so screen readers can announce the boundary). */
  label?: string
  items: FbDropdownItem[]
}

interface FbDropdownProps {
  /** Text shown on the trigger button. */
  label: string
  /** Flat list of items — use this OR sections, not both. */
  items?: FbDropdownItem[]
  /** Grouped sections with an optional separator between them. */
  sections?: FbDropdownSection[]
  /** Fired when the user selects an item. Receives the item key. */
  onAction?: (key: string) => void
  /** Currently-selected item key — when set, the matching item renders with
   *  the active-state styling. Required for React Aria to write `data-selected`
   *  so the `selected:` Tailwind variants in `FbDropdownMenuItem` actually fire. */
  selectedKey?: string
  /** Additional classes on the trigger button. */
  className?: string
  /** Place the menu above the trigger instead of below. */
  placement?: 'bottom start' | 'bottom end' | 'top start' | 'top end'
}

/**
 * Single trigger button styled to the Figma spec.
 *
 * white bg · border-neutral-300 · rounded-lg · h-11
 * font-medium text-sm text-neutral-800 tracking-[-0.2px]
 * caret icon on the right
 */
export function FbDropdown({
  label,
  items = [],
  sections,
  onAction,
  selectedKey,
  className,
  placement = 'bottom start',
}: FbDropdownProps) {
  return (
    <MenuTrigger>
      <Button
        className={cn(
          'inline-flex items-center gap-1.5 h-11 px-4 rounded-lg',
          'bg-white border border-neutral-300',
          'text-sm font-medium tracking-[-0.2px] text-neutral-800 whitespace-nowrap',
          'transition-colors hover:bg-surface-sunken pressed:bg-neutral-100',
          'outline-none focus-visible:ring-2 focus-visible:ring-brand-focus',
          className
        )}
      >
        <span>{label}</span>
        <PiCaretDownBold size={14} className="text-neutral-500" />
      </Button>

      <Popover
        placement={placement}
        className={cn(
          'z-50 w-[240px] bg-white rounded-xl overflow-hidden outline-none',
          // Figma shadow-md spec
          'shadow-[0px_4px_4px_rgba(16,24,40,0.10),0px_2px_2px_rgba(16,24,40,0.06)]',
          // Subtle border outline
          '[filter:drop-shadow(0_1px_0px_#e5e7eb)_drop-shadow(0_-1px_0px_#e5e7eb)_drop-shadow(1px_0_0px_#e5e7eb)_drop-shadow(-1px_0_0px_#e5e7eb)]',
          // Animate in
          'entering:animate-in entering:fade-in entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out exiting:zoom-out-95'
        )}
      >
        <Menu
          onAction={(key) => onAction?.(String(key))}
          selectionMode={selectedKey !== undefined ? 'single' : 'none'}
          selectedKeys={selectedKey !== undefined ? [selectedKey] : undefined}
          disallowEmptySelection={selectedKey !== undefined}
          className="outline-none p-1.5 max-h-[252px] overflow-y-auto"
        >
          {sections
            ? sections.map((section, i) => (
                <React.Fragment key={section.key}>
                  <Section aria-label={section.label}>
                    {section.items.map((item) => (
                      <FbDropdownMenuItem key={item.key} item={item} />
                    ))}
                  </Section>
                  {i < sections.length - 1 && (
                    <Separator className="my-1.5 h-px bg-line-subtle" />
                  )}
                </React.Fragment>
              ))
            : items.map((item) => (
                <FbDropdownMenuItem key={item.key} item={item} />
              ))}
        </Menu>
      </Popover>
    </MenuTrigger>
  )
}

/** Individual menu item styled to the Figma spec. */
function FbDropdownMenuItem({ item }: { item: FbDropdownItem }) {
  return (
    <MenuItem
      id={item.key}
      className={cn(
        'flex items-center gap-2.5 px-3 py-[10px] rounded-lg cursor-pointer outline-none',
        'text-sm font-medium tracking-[-0.2px] text-neutral-700',
        'hover:bg-neutral-100 hover:text-neutral-800',
        'data-[hovered]:bg-neutral-100 data-[hovered]:text-neutral-800',
        'focus:bg-neutral-100 focus:text-neutral-800',
        'data-[focused]:bg-neutral-100 data-[focused]:text-neutral-800',
        'selected:bg-neutral-100 selected:text-neutral-800'
      )}
    >
      {item.icon && (
        <span className="flex-shrink-0 text-neutral-500">{item.icon}</span>
      )}
      {item.label}
    </MenuItem>
  )
}
